import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  Actions,
  ColumnKeys,
  CustomRulesWebComponentData,
  CustomRulesWebComponentSchema,
  GridType,
  RuleType,
  Status,
} from '../types';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_GZ_IMAGE_REPO } from '../../../webcomponents.constants';
import { TemplateState } from '../../../types/common/types';
import { CustomRulesDataProvider } from '../dataProvider/CustomRulesDataProvider';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents, SelectOption } from '../../../ui/gzSelect/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzImageRepoWebComponent } from '../../../ui/gzImageRepo';
import {
  renderCompanyName,
  renderCreatedOn,
  renderLastUpdatedOn,
  renderRuleName,
  renderRuleType,
  renderTags,
} from './CustomGridRenderers';
import { translateFn } from '../../../utils/translator.helper';
import { START_YARA_SCAN } from '../../addRule/constants';
import { Feature, Rights } from '@nirvana/shared/constants';
import { prepareRuleForBackendProcessing } from '../utils';

export class CustomRulesView extends BaseWebComponentView {
  public changeStatusEnable: SelectOption;
  public changeStatusDisable: SelectOption;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('.title');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$addRuleButton = this.shadow.querySelector('#addButton');
    this.dom.$changeStatusButtonText = this.shadow.querySelector('#changeStatusText');
    this.dom.$changeStatusSelect = this.shadow.querySelector('#changeStatusSelect');
    this.dom.$deleteButton = this.shadow.querySelector('#deleteButton');
    this.dom.$customRulesAdditionalText = this.shadow.querySelector('#customRulesAdditionalText');
    this.dom.$addEmptyStateButton = this.shadow.querySelector('#addButtonEmptyState');
    this.dom.$sideMenu = this.shadow.querySelector('#sideMenu');
  }

  buildSuccessView(data: CustomRulesWebComponentSchema) {
    this.buildButtons();
    this.buildGridView(data.data, data.externalFilters);
  }

  getChangeStatusOptions() {
    this.changeStatusEnable = {
      title: translateFn('Enable'),
      value: Actions.ENABLE,
      disabled: true,
    };

    this.changeStatusDisable = {
      title: translateFn('Disable'),
      value: Actions.DISABLE,
      disabled: true,
    };

    return [this.changeStatusEnable, this.changeStatusDisable];
  }

  buildButtons() {
    const disableButtons = !Application.Main.hasAllRights([
      Rights.RIGHT_MANAGE_INVENTORY,
      Rights.MANAGE_POLICIES_WRITE,
    ]);
    this.dom.$addRuleButton.innerText = translateFn('Add rule');
    this.dom.$addRuleButton.disabled = disableButtons;

    this.dom.$changeStatusButtonText.innerText = translateFn('Change status');
    this.dom.$changeStatusSelect.disabled = true;
    this.dom.$changeStatusSelect.options = this.getChangeStatusOptions();

    this.dom.$deleteButton.innerText = translateFn('Delete');

    this.dom.$addEmptyStateButton.innerText = translateFn('Add rule');
    this.dom.$addEmptyStateButton.disabled = disableButtons;
  }

  buildGridView(data: CustomRulesWebComponentData, externalFilters: Array<any> = []) {
    const defaultView = data.defaultViewProps;
    this.dom.$grid.propertiesSet = false;

    this.dom.$grid.dataProvider = new CustomRulesDataProvider(this.dependencies);
    this.dom.$grid.viewId = defaultView.id;
    this.dom.$grid.allColumnDetails = data.allColumns;
    this.dom.$grid.columns = defaultView.displayedColumns;
    this.dom.$grid.hasFilters = true;
    this.dom.$grid.sideMenuParent = this.shadow;

    if (externalFilters.length > 0) {
      for (const externalFilter of externalFilters) {
        const filterName = Object.keys(externalFilter)[0];

        const filterIndex = defaultView.filters.findIndex(elem => {
          return Object.keys(elem).includes(filterName);
        });

        if (filterIndex === -1) {
          defaultView.filters.push(externalFilter);
        } else {
          defaultView.filters[filterIndex] = externalFilter;
        }
      }
    }

    this.dom.$grid.filters = defaultView.filters;
    this.dom.$grid.filtersUiConfigs = [
      {
        key: ColumnKeys.CREATED_ON,
        config: {
          enableSelectAllToDate: true,
        },
      },
      {
        key: ColumnKeys.LAST_UPDATED_ON,
        config: {
          enableSelectAllToDate: true,
        },
      },
    ];
    this.dom.$grid.isCompactViewChecked = false;
    this.dom.$grid.defaultViewProps = defaultView;
    this.dom.$grid.pageSize = defaultView.pageSize;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.RULE_NAME]: this.renderGridRuleName.bind(this),
      [ColumnKeys.CREATED_ON]: this.renderGridCreatedOn.bind(this),
      [ColumnKeys.LAST_UPDATED_ON]: this.renderGridLastUpdatedOn.bind(this),
      [ColumnKeys.RULE_TYPE]: this.renderGridRuleType.bind(this),
      [ColumnKeys.TAGS]: this.renderGridTags.bind(this),
      [ColumnKeys.INLINE_ACTIONS]: this.renderInlineActions.bind(this),
      [ColumnKeys.COMPANY]: this.renderCompany.bind(this),
    };

    this.dom.$grid.sortColumnName = ColumnKeys.CREATED_ON;
    this.dom.$grid.sortDirection = 'ASC';
    const predefinedFiltersKeys = [ColumnKeys.RULE_NAME, ColumnKeys.LAST_UPDATED_ON, ColumnKeys.STATUS];

    if (this.dependencies.isPartnerCompany()) {
      predefinedFiltersKeys.push(ColumnKeys.COMPANY);
    }
    this.dom.$grid.predefinedFiltersKeys = predefinedFiltersKeys;

    if (this.dependencies.gridType === GridType.DETECTION) {
      this.dom.$title.textContent = translateFn('Custom detection rules');
      this.dom.$grid.customStrings = {
        noSearchResultsString: translateFn('Start adding detection rules', {}),
      };
      this.dom.$customRulesAdditionalText.innerText = translateFn("You don't have any detection rules defined yet.");
    } else {
      this.dom.$title.textContent = translateFn('Custom exclusion rules');
      this.dom.$grid.customStrings = {
        noSearchResultsString: translateFn('Start adding exclusion rules', {}),
      };
      this.dom.$customRulesAdditionalText.innerText = translateFn("You don't have any exclusion rules defined yet.");
    }

    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$sideMenu.closeSideMenu();

    this.dom.$grid.propertiesSet = true;
    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }

    this.changeEmptyStateIcon();
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private renderGridRuleName(rowData: string) {
    return renderRuleName(
      rowData['item'],
      this.dom.$grid,
      this.dependencies.getActiveCompany(),
      this.dependencies.isPartnerCompany(),
      rowData['item'].availableCompanies,
    );
  }

  private renderGridCreatedOn(rowData: string) {
    return renderCreatedOn(rowData['item']);
  }

  private renderGridLastUpdatedOn(rowData: string) {
    return renderLastUpdatedOn(rowData['item']);
  }

  private renderGridRuleType(rowData: string) {
    return renderRuleType(rowData['item']);
  }

  private renderGridTags(rowData: string) {
    return renderTags(rowData['item']);
  }

  private renderCompany(rowData: string) {
    return renderCompanyName(rowData['item']);
  }

  private changeEmptyStateIcon() {
    const emptyStateContainer = this.dom.$grid.shadowRoot?.querySelector(
      WEB_COMPONENT_GZ_IMAGE_REPO,
    ) as GzImageRepoWebComponent;
    if (!emptyStateContainer) {
      return;
    }
    if (emptyStateContainer.state === 'empty') {
      emptyStateContainer.setAttribute('state', 'start');
    }
  }

  private isActionDisabled(rowData, availableCompanies, endpointTags, selectedCompanies) {
    if (!Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY, Rights.MANAGE_POLICIES_WRITE])) {
      return true;
    }

    const isPartner = this.dependencies.isPartnerCompany();
    const company = this.dependencies.getActiveCompany();

    const availableCompanyIds = availableCompanies.map(item => item._id);
    const tagCompanies = endpointTags.map(item => item.companyId);

    if (availableCompanies.length > 0) {
      if (isPartner && endpointTags.length > 0) {
        const isCompanyNotIncluded = !availableCompanyIds.includes(rowData['item'].companyId) && rowData['item'].companyId !== company.companyId;
        const hasUnavailableTags = tagCompanies.filter(item => !availableCompanyIds.includes(item)).length !== 0;
        return isCompanyNotIncluded || hasUnavailableTags;
      } else {
        const hasUnavailableCompanies = selectedCompanies.filter(item => !availableCompanyIds.includes(item)).length !== 0;
        return !availableCompanyIds.includes(rowData['item'].companyId) || hasUnavailableCompanies;
      }
    }

    return false;
  }

  private createSelectElement() {
    const select = document.createElement('gz-select') as GzSelectWebComponent;
    select.style.cssText = 'height: 100%; display: flex; align-items: center;';
    select.innerHTML =
      `<gz-icon-button icon="ds-icon-more" buttoncolor="secondary" buttontype="square" ` +
      `fontsize="22" size="24" buttonfill="quiet" class="inline-menu-trigger"></gz-icon-button`;
    return select;
  }

  private getSelectOptions(rowData, availableCompanyIds, tagCompanies, disabled) {
    const selectOptions: any[] = [];
    if (rowData['item']['type'] === RuleType.YARA) {
      const hasAvailableCompaniesForYaraScan = tagCompanies.filter(item => availableCompanyIds.includes(item)).length !== 0 || availableCompanyIds.includes(rowData['item'].companyId);
      selectOptions.push({
        title: translateFn('Scan'),
        value: Actions.SCAN,
        disabled: !hasAvailableCompaniesForYaraScan,
      });
    }
    if (rowData['item']['status'] === Status.ENABLED) {
      selectOptions.push({
        title: translateFn('Disable'),
        value: Actions.DISABLE,
        disabled,
      });
    } else if (rowData['item']['status'] === Status.DISABLED) {
      selectOptions.push({
        title: translateFn('Enable'),
        value: Actions.ENABLE,
        disabled,
      });
    }
    selectOptions.push({
      title: translateFn('Delete'),
      value: Actions.DELETE,
      customClass: 'danger',
      disabled: disabled,
    });
    return selectOptions;
  }

  private async handleSelectionChange(event: WebComponentEvent, rowData) {
    if (event.detail.selected === Actions.DELETE) {
      this.deleteRule([rowData['item']]);
    } else if (event.detail.selected === Actions.ENABLE) {
      this.changeStatus(Status.ENABLED, [rowData['item']]);
    } else if (event.detail.selected === Actions.DISABLE) {
      this.changeStatus(Status.DISABLED, [rowData['item']]);
    } else if (event.detail.selected === Actions.SCAN) {
      this.dom.$wrapper.dispatchEvent(
        new CustomEvent(START_YARA_SCAN, {
          detail: {
            rule: rowData['item'],
          },
        }),
      );
    }
  }

  private renderInlineActions(rowData: string) {
    const availableCompanies = rowData['item'].availableCompanies ?? [];
    const availableCompanyIds = availableCompanies.map(item => item._id);

    const endpointTags = rowData['item'].targets?.endpointTags ?? [];
    const tagCompanies = endpointTags.map(item => item.companyId);

    const selectedCompanies = rowData['item'].targets?.companiesIds ?? [];

    const disabled = this.isActionDisabled(rowData, availableCompanies, endpointTags, selectedCompanies);
    const select = this.createSelectElement() as GzSelectWebComponent;
    select.options = this.getSelectOptions(rowData, availableCompanyIds, tagCompanies, disabled);

    select.addEventListener(SelectEvents.SELECTION_CHANGED, async (event: WebComponentEvent) => {
      await this.handleSelectionChange(event, rowData);
    });

    return select;
  }

  public deleteRule(items) {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML =
      items.length > 1
      ? `<p> ${translateFn(
        'Do you want to delete the selected rules? Rules created by partners for your company cannot be deleted.',
      )} </p>`
      : `<p> ${translateFn(
        'Do you want to delete this rule? Rules created by partners for your company cannot be deleted.',
      )} </p>`;

    const dialogFooter = document.createElement('div');
    const deleteButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();
    deleteButton.innerText = translateFn('Delete');
    deleteButton.setAttribute('type', 'outline');
    deleteButton.setAttribute('color', 'negative');
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    dialogFooter.appendChild(deleteButton);
    dialogFooter.appendChild(cancelButton);

    const dialogTitle = translateFn('Delete');

    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    deleteButton.addEventListener('click', async () => {
      try {
        await this.dependencies.api.clients.CustomRules.deleteRecords(items).toPromise();
        await this.dom.$grid.controller.handleGridItemsLoad(true, true);
        this.showToastAfterDelete(items.length);
      } catch (error) {
        await this.dom.$grid.controller.handleGridItemsLoad(true, true);
        this.showToastDeleteError(items.length);
      }
      this.dom.$sideMenu.closeSideMenu();
      this.dom.$sideMenu.setMenuContent(null);
      dialog.close();
    });

    cancelButton.addEventListener('click', () => {
      dialog.close();
    });
  }

  private showToastAfterDelete(numberOfRules) {
    const message = numberOfRules > 1 ? translateFn('Rules deleted.') : translateFn('Rule deleted.');
    const toastConfig: GzToast = {
      message: message,
      severity: ToastSeverity.SUCCESS,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  private showToastDeleteError(numberOfRules) {
    const message = numberOfRules > 1 ? translateFn('Failed to delete rules.') : translateFn('Failed to delete rule.');
    const toastConfig: GzToast = {
      message: message,
      severity: ToastSeverity.ERROR,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  public changeStatus(status: Status, items) {
    const numberOfRules = items.filter(item => item.status !== status).length;

    let dialogBodyMessage;
    let dialogTitle;
    const isGz = this.dependencies.isGZ();

    if (items.length > 1) {
      dialogBodyMessage = status
                          ? `<p> ${translateFn('The status of the selected rules will change to <b>Enabled</b>.')} </p>`
                          : `<p> ${translateFn('The status of the selected rules will change to <b>Disabled</b>.')} </p>`;
      dialogBodyMessage += `<p> ${translateFn(
        `<b>{NUMBER_OF_RULES} rules</b> will be impacted by this change.`,
        {
          NUMBER_OF_RULES: numberOfRules,
        },
      )} </p>`;

      dialogTitle = status ? translateFn('Enable rules?') : translateFn('Disable rules?');
    } else {
      dialogBodyMessage = status
                          ? `<p> ${translateFn(
          `The status of the selected rule will change to <b>Enabled</b>.`,
        )} </p>`
                          : `<p> ${translateFn(
          `The status of the selected rule will change to <b>Disabled</b>.`,
        )} </p>`;

      dialogTitle = status ? translateFn('Enable rule?') : translateFn('Disable rule?');
    }
    if (!isGz) {
      dialogBodyMessage += `<p>${translateFn('Rules created by partners for your company cannot be changed.')}</p>`;
    }

    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = dialogBodyMessage;

    const dialogFooter = document.createElement('div');
    const confirmButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();
    confirmButton.innerText = translateFn('Confirm');
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    dialogFooter.appendChild(confirmButton);
    dialogFooter.appendChild(cancelButton);

    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    confirmButton.addEventListener('click', async () => {
      if (confirmButton.showSpinner) {
        return;
      }

      try {
        confirmButton.showSpinner = true;
        items = items.map(rule => {
          rule.status = status;

          return prepareRuleForBackendProcessing(
            rule,
            this.dependencies.gridType,
            this.dependencies.platformHasFeature(Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS),
          );
        });
        await this.dependencies.api.clients.CustomRules.updateRecords(items).toPromise();
        await this.dom.$grid.controller.handleGridItemsLoad(true, true);
        this.showToastAfterChangeStatus(status);
      } catch (error) {
        await this.dom.$grid.controller.handleGridItemsLoad(true, true);
        this.showToastChangeStatusError(status, items.length);
      } finally {
        dialog.close();
      }
    });

    cancelButton.addEventListener('click', () => {
      dialog.close();
    });
  }

  private showToastAfterChangeStatus(status: Status) {
    const message =
      status === Status.ENABLED
      ? translateFn('Successfully changed status to Enabled.')
      : translateFn('Successfully changed status to Disabled.');

    const toastConfig: GzToast = {
      message: message,
      severity: ToastSeverity.SUCCESS,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  private showToastChangeStatusError(status: Status, numberOfItems) {
    let message;
    if (status === Status.ENABLED) {
      message = numberOfItems > 1 ? translateFn('Failed to enable rules. EDR license missing.') : translateFn('Failed to enable rule. EDR license missing.');
    } else {
      message = numberOfItems > 1 ? translateFn('Failed to disable rules. EDR license missing.') : translateFn('Failed to disable rule. EDR license missing.');
    }
    const toastConfig: GzToast = {
      message: message,
      severity: ToastSeverity.ERROR,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }
}
